<script setup lang="ts">
const { isNavbarOpen } = storeToRefs(useLayoutStore())

const squigglyLinePath = useTemplateRef('squigglyLinePath')
const squigglyLinePathVisible = ref(false)

const initialMockups = [
  { src: '/drawn-mockup.webp', alt: 'Esboço de um website feito a mão' },
  { src: '/code.webp', alt: 'Pedaço do código fonte de um website' },
]

const finalProducts = [
  { src: '/final-product.webp', alt: 'Exemplos de produtos finais' },
]

whenever(squigglyLinePath, (path) => {
  const pathLength = path.getTotalLength()
  path.style.strokeDasharray = `${pathLength}`
  path.style.strokeDashoffset = `${pathLength}`
  setTimeout(() => squigglyLinePathVisible.value = true, 300)
})
</script>

<template>
  <section id="sobre">
    <div
      :class="isNavbarOpen ? 'pt-0' : 'pt-10 md:pt-16'"
      class="container flex flex-col items-center px-8 relative transition-[padding] duration-500 ease-in-out pb-16"
    >
      <!-- doodles -->
      <!-- <Icon
        name="dddoodle:misc-55"
        class="text-primary text-5xl md:text-7xl absolute top-0 right-2 animate-doodle"
      /> -->
      <!-- title -->
      <h1 class="text-4xl/10 md:text-5xl/tight font-bold text-center mb-12">
        <div class="animate-in fill-mode-both fade-in slide-in-from-top-10 duration-400 ease-out">
          Transforme a
        </div>
        <div class="animate-in fill-mode-both fade-in slide-in-from-top-10 duration-400 ease-out delay-300">
          sua ideia em
        </div>
        <div class="animate-in fill-mode-both fade-in slide-in-from-top-10 duration-400 delay-600 inline-block">
          <div class="text-primary italic relative z-10 opacity-90">
            realidade
          </div>
          <div
            class="absolute -top-3.5 -left-4 w-[calc(100%+2rem)] opacity-0 transition-opacity duration-1000"
            :class="{ 'opacity-80': squigglyLinePathVisible }"
          >
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:svgjs="http://svgjs.dev/svgjs" viewBox="0 0 800 400">
              <path
                ref="squigglyLinePath"
                d="M43.946189880371094,251.12107849121094C47.23468017578125,248.57997131347656,54.7085215250651,234.52914683024088,63.67713165283203,235.8744354248047C72.64574178059895,237.2197240193685,85.35127385457356,258.744379679362,97.75785064697266,259.19281005859375C110.16442743937175,259.6412404378255,124.81315485636394,238.11658223470053,138.11659240722656,238.5650177001953C151.4200299580892,239.0134531656901,162.92974599202475,262.4813283284505,177.57847595214844,261.8834228515625C192.22720591227213,261.2855173746745,211.06128692626953,234.2302042643229,226.00897216796875,234.9775848388672C240.95665740966797,235.72496541341147,250.37370808919272,265.62032318115234,267.26458740234375,266.3677062988281C284.1554667154948,267.1150894165039,309.2675526936849,240.0597890218099,327.354248046875,239.46188354492188C345.4409434000651,238.86397806803384,356.2032928466797,262.6307957967122,375.7847595214844,262.7802734375C395.36622619628906,262.9297510782878,428.1016438802083,241.10613505045572,444.8430480957031,240.35874938964844C461.58445231119794,239.61136372884116,459.34230041503906,258.29595947265625,476.2331848144531,258.29595947265625C493.1240692138672,258.29595947265625,527.6532236735026,240.0597941080729,546.1883544921875,240.35874938964844C564.7234853108724,240.65770467122397,568.0119832356771,261.4349822998047,587.4439697265625,260.0896911621094C606.8759562174479,258.74440002441406,641.4050903320312,235.27654012044272,662.7802734375,232.28700256347656C684.1554565429688,229.2974650065104,696.1136067708334,247.0852025349935,715.695068359375,242.1524658203125C735.2765299479166,237.2197291056315,769.5067138671875,209.26756286621094,780.26904296875,202.69058227539062"
                fill="none"
                stroke-width="15"
                stroke="var(--primary)"
                stroke-linecap="round"
                :class="{ 'draw-path': squigglyLinePathVisible }"
              />
            </svg>
          </div>
        </div>
      </h1>
      <p class="text-lg font-mono text-muted-foreground pl-1 animate-in fill-mode-both fade-in duration-500 delay-1000 max-w-xl text-center mb-8 md:mb-12">
        Criamos websites e apps para empresas e profissionais que querem se destacar no mercado.
      </p>

      <div class="flex items-center h-10 mb-16 -space-x-2">
        <span class="text-secondary text-xl font-mono font-semibold tracking-tighter">do início</span>
        <div class="w-28 pt-6">
          <Icon
            name="dddoodle:arrow-9"
            mode="svg"
            class="size-full text-secondary -scale-y-100"
            width="100%"
            height="100%"
          />
        </div>
      </div>
      <HomeHeroSectionMockupSlideshow :cards="initialMockups" />

      <div class="flex items-center h-24 mb-16 relative">
        <span class="text-secondary text-xl font-mono font-semibold tracking-tighter">ao fim</span>
        <div class="w-44 pt-8 absolute left-1/2 -translate-x-1/2">
          <Icon
            name="dddoodle:arrow-9-custom"
            mode="svg"
            viewBox="0 0 600 600"
            class="size-full text-secondary -scale-x-100"
            width="100%"
            height="100%"
          />
        </div>
      </div>
      <HomeHeroSectionMockupSlideshow :cards="finalProducts" />
    </div>
  </section>
</template>

<style scoped>
.draw-path {
  animation: draw 2s ease-in-out forwards;
}

@keyframes draw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
